
import { Component, Provide, Mixins, Prop } from "vue-property-decorator";
import { FilteringSortingTableStore } from "@/mixins/tableWithFilteringAndSortingStore";
import Context from "@/mixins/context";
import { IFilter, ISorter, ITableConfig } from "@/models/tables";
import { DataModules } from "@/store/modules/data";
import { CreateElement } from "vue";
import { IClient } from "@/models/clients";
import { Sorters } from "@/data/table";
import {
  ICProdsProviderData,
  ICProdsProviderMethods
} from "@/models/providers/contractProduct";

@Component
export default class CProdsProvider extends Mixins(
  FilteringSortingTableStore,
  Context
) {
  // Props

  @Prop({ default: null })
  public clientId!: IClient["id"];

  @Prop({ default: "CPL" })
  public declare id: string;

  @Prop({ default: 3 })
  public declare limit: number;

  @Prop({ default: true })
  public declare handleLoading: boolean;

  @Prop({ default: true })
  public declare handleRequestError: boolean;

  @Prop({ default: () => ({}) })
  public declare manualFilters: Record<string, string | number | boolean>;

  @Prop({ default: () => [] })
  public declare availableFilters: IFilter[];

  @Prop({ default: () => [] })
  public declare availableSorters: ISorter[];

  // Data

  public dataModule = DataModules.CONTRACTS_PRODUCTS;
  public debounceSearch = this.$_.debounce(this.doQuickSearch, 250);
  public defaults: ITableConfig = {
    page: 1,
    limit: this.limit,
    sort: {
      field: "created_at",
      direction: Sorters.DESCENDING
    },
    filters: []
  };
  public isProcessing = false;
  public searchQuery = "";

  // Computed

  public get clientFilter() {
    return this.clientId ? { "filter[clients.id]": this.clientId } : {};
  }

  public get queryFilter() {
    return this.searchQuery ? { query: this.searchQuery } : {};
  }

  public get hasProducts(): boolean {
    return !!this.table.data.length;
  }

  public get canShowMore(): boolean {
    return this.table.total > this.table.data.length;
  }

  // Life-cycle Methods

  public render(h: CreateElement) {
    // Handle loading
    if (this.handleLoading && this.loading)
      return h("is-loading", { props: { if: true } });
    // Handle request error
    if (this.handleRequestError && this.requestError)
      return h("request-error", {
        props: { isLoading: this.table.loading && this.requestError },
        on: { click: () => this.getData() }
      });
    return (
      this.$scopedSlots?.default &&
      this.$scopedSlots.default({
        $cProdsData: this.cProdsProvider(),
        $cProdsMethods: this.cProdsProviderMethods
      })
    );
  }

  public created() {
    this.filters = this.availableFilters.filter(i =>
      i.if ? i.if(this) : true
    );
    this.getData();
  }

  // Methods

  public getStoreParams(params: any): Promise<any> {
    return this.$_.merge(
      {
        params: {
          with: this.$store.getters[
            `data/${DataModules.CONTRACTS_PRODUCTS}/withParam`
          ]({ clientId: this.clientId }),
          ...this.queryFilter,
          ...this.clientFilter,
          ...this.manualFilters
        }
      },
      params // Important – must merge last
    );
  }

  public async reloadData() {
    await this.cancelRequests();
    return this.getData({ ignoreStored: true });
  }

  public doQuickSearch(value = "") {
    if (value === this.searchQuery) return Promise.resolve();
    this.searchQuery = value;
    return this.reloadData();
  }

  // Provide

  @Provide()
  public get cProdsProviderMethods(): ICProdsProviderMethods {
    return {
      doQuickSearch: this.doQuickSearch,
      onFiltersChange: this.onFiltersChange,
      onLimitChange: this.onLimitChange,
      onPageChange: this.onPageChange,
      onSort: this.onSort,
      reloadData: this.reloadData
    };
  }

  @Provide()
  public cProdsProvider(): ICProdsProviderData {
    return {
      availableFilters: this.filters,
      availableSorters: this.availableSorters,
      activeFilters: this.activeFilters,
      activeSort: this.table.sort,
      canShowMore: this.canShowMore,
      clientId: this.clientId,
      debounceSearch: this.debounceSearch,
      enableFiltering: this.enableFiltering,
      enableSorting: this.enableSorting,
      hasProducts: this.hasProducts,
      isLoading: this.table.loading,
      limit: this.table.limit,
      page: this.table.page,
      products: this.table.data,
      searchQuery: this.searchQuery,
      table: this.table,
      total: this.table.total
    };
  }
}
